<script>
/**
 * 감정평가 현물관리 — appraisal_item CRUD (본사 전용).
 * 금/은 탭, 목록(일부 컬럼 제외), 수정 모달, 소프트 삭제.
 */
import { getAppraisalItemList, deleteAppraisalItem } from '@/api/appraisalItem.js'
import { getAdminInfo } from '@/utils/token.js'
import AppraisalSpotItemFormDialog from './AppraisalSpotItemFormDialog.vue'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'AppraisalSpotItemPage',

  components: { AppraisalSpotItemFormDialog, AppPagination },

  data() {
    return {
      adminInfo: {},
      metalTab: 'GOLD',
      items: [],
      page: 1,
      size: 10,
      loading: false,
      formDialog: false,
      selectedRow: null,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      deleteErrorMsg: '',
    }
  },

  computed: {
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    spotTypeParam() {
      return this.metalTab === 'SILVER' ? 'SILVER' : 'GOLD'
    },
    total() {
      return this.items.length
    },
    pagedItems() {
      const start = (this.page - 1) * this.size
      return this.items.slice(start, start + this.size)
    },
  },

  watch: {
    metalTab() {
      this.page = 1
      this.fetchList()
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    this.fetchList()
  },

  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getAppraisalItemList(this.spotTypeParam)
        if (res.data?.code === 0) {
          this.items = Array.isArray(res.data.data) ? res.data.data : []
          if ((this.page - 1) * this.size >= this.items.length) {
            this.page = 1
          }
        } else {
          this.items = []
          this.page = 1
        }
      } catch (e) {
        console.error('[AppraisalSpotItemPage] fetchList', e)
        this.items = []
        this.page = 1
      } finally {
        this.loading = false
      }
    },
    onPageChange(newPage) {
      this.page = newPage
    },
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
    },

    spotTypeLabel(code) {
      if (code === 'GOLD') return '금'
      if (code === 'SILVER') return '은'
      return code || '-'
    },

    formatPercent(v) {
      if (v == null || v === '') return '-'
      return String(v)
    },

    openCreate() {
      this.selectedRow = null
      this.formDialog = true
    },

    openEdit(item) {
      this.selectedRow = { ...item }
      this.formDialog = true
    },

    onSaved() {
      this.selectedRow = null
      this.fetchList()
    },

    openDelete(item) {
      this.deleteTarget = item
      this.deleteErrorMsg = ''
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.deleteTarget) return
      this.deleting = true
      this.deleteErrorMsg = ''
      try {
        const res = await deleteAppraisalItem(this.deleteTarget.appraisalItemCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.deleteTarget = null
          this.fetchList()
        } else {
          this.deleteErrorMsg = res.data?.message || '삭제 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[AppraisalSpotItemPage] delete', e)
        this.deleteErrorMsg = '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-1 flex-wrap ga-2">
      <div>
        <div class="text-h5 font-weight-bold">감정평가 현물관리</div>
        <div class="text-body-2 text-grey mt-1">
          감정항목(금·은) 기준비율·환산비율 관리 — 본사 관리자 전용
        </div>
      </div>
      <v-btn
        v-if="isHq"
        color="primary"
        prepend-icon="mdi-plus"
        @click="openCreate"
      >
        항목 등록
      </v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mt-4">
      <v-tabs v-model="metalTab" color="primary" grow>
        <v-tab value="GOLD" class="font-weight-medium">금 (GOLD)</v-tab>
        <v-tab value="SILVER" class="font-weight-medium">은 (SILVER)</v-tab>
      </v-tabs>
      <v-divider />

      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          감정항목 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th>감정항목코드</th>
            <th class="w-1">현물구분</th>
            <th class="w-1">감정항목유형</th>
            <th>감정항목명</th>
            <th class="w-1">기준비율</th>
            <th class="w-1">환산비율</th>
            <th class="w-1">수정일</th>
            <th v-if="isHq" class="w-1 text-center">작업</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in pagedItems" :key="item.appraisalItemCode">
            <td class="text-caption text-grey-darken-1">{{ item.appraisalItemCode }}</td>
            <td class="text-caption">{{ spotTypeLabel(item.spotType) }}</td>
            <td class="text-caption">{{ item.appraisalItemType || '-' }}</td>
            <td class="font-weight-medium text-body-2">{{ item.appraisalItemName || '-' }}</td>
            <td class="text-caption">{{ formatPercent(item.standardPercent) }}</td>
            <td class="text-caption">{{ formatPercent(item.conversionPercent) }}</td>
            <td class="text-caption">{{ item.updateDate || '-' }}</td>
            <td v-if="isHq" class="text-center text-nowrap">
              <v-btn
                size="small"
                variant="tonal"
                color="primary"
                class="mr-1"
                @click="openEdit(item)"
              >
                수정
              </v-btn>
              <v-btn
                size="small"
                variant="tonal"
                color="error"
                @click="openDelete(item)"
              >
                삭제
              </v-btn>
            </td>
          </tr>
          <tr v-if="!loading && total === 0">
            <td :colspan="isHq ? 8 : 7" class="text-center text-grey py-10">
              등록된 항목이 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>

    <AppraisalSpotItemFormDialog
      v-model="formDialog"
      :edit-data="selectedRow"
      :default-spot-type="spotTypeParam"
      @saved="onSaved"
    />

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-2">항목 삭제</v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-alert v-if="deleteErrorMsg" type="error" density="compact" class="mb-3" rounded="lg">
            {{ deleteErrorMsg }}
          </v-alert>
          <p class="text-body-2">
            선택한 감정항목을 삭제(비활성)합니다. 계속하시겠습니까?
          </p>
          <p v-if="deleteTarget" class="text-caption text-grey mt-2">
            {{ deleteTarget.appraisalItemName }} ({{ deleteTarget.appraisalItemCode }})
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="confirmDelete">
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
